<template>
    <div>
        <div class="flex items-center justify-between">
            <breadcrumbs>Banen</breadcrumbs>
            <link-button :href="route('admin.jobtemplates.create')" icon="plus" class="bg-white">Nieuw baan</link-button>
        </div>

        <h1 class="text-5xl text-teal-800">Banen</h1>

        <div>

            <div class="flex justify-between items-center mb-5">
                <div class="flex flex-col">
                    <search-filter v-model="form.search" class="w-full max-w-sm mr-4" @reset="reset" @input="submitSearch">
                        <label class="block text-grey-darkest">Trashed:</label>
                        <select v-model="form.trashed" class="mt-1 w-full form-select" @change="reloadData">
                            <option :value="null" />
                            <option value="with">With Trashed</option>
                            <option value="only">Only Trashed</option>
                        </select>
                    </search-filter>

                    <active-filters :filters="filters" @remove-filter="removeFilter" />
                </div>

                <num-results name="banen"
                             :from="jobtemplates.from"
                             :to="jobtemplates.to"
                             :total="jobtemplates.total"
                />

                <input type="hidden" v-model="form.orderBy" />
                <input type="hidden" v-model="form.sortOrder" />
            </div>

            <p v-if="!jobtemplates.data.length">Er zijn geen banen gevonden.</p>

            <table v-else class="w-full whitespace-no-wrap bg-white rounded">
                <thead>
                    <tr class="text-teal-800 font-bold">
                        <td class="text-center" width="10">
                            <a v-if="filters.trashed" href @click.prevent="sort('deleted_at')">
                                <i class="fad fa-trash mr-1"></i><sort-icon col="deleted_at" :orderby="filters.orderBy" :sortorder="filters.sortOrder" />
                            </a>
                        </td>
                        <th class="px-3 py-3 text-left">
                            <a href @click.prevent="sort('id')" class="hover:underline">ID<sort-icon col="id" :orderby="filters.orderBy" :sortorder="filters.sortOrder" /> </a>
                        </th>
                        <td class="px-3 py-3">
                            <a href @click.prevent="sort('created_at')" class="hover:underline">
                                <i class="fal fa-calendar-plus mr-1"></i>Created<sort-icon col="created_at" :orderby="filters.orderBy" :sortorder="filters.sortOrder" />
                            </a>
                        </td>

                        <td class="px-3 py-3">
                            <a href @click.prevent="sort('title')" class="hover:underline">
                                Title<sort-icon col="title" :orderby="filters.orderBy" :sortorder="filters.sortOrder" />
                            </a>
                        </td>

                        <td class="px-3 py-3">
                            Negeer
                        </td>

                        <td width="200"></td>
                    </tr>
                </thead>

                <tr
                    v-for="(jobtemplate, index) in jobtemplates.data"
                    :key="jobtemplates.id"
                    class="hover:text-teal-900 hover:bg-teal-300 focus-within:bg-teal-300 :text-teal-900"
                    :class="{ 'bg-teal-100': !(index % 2), 'text-gray-500': jobtemplate.deleted_at }"
                >
                    <td class="border-t" width="10">
                        <span v-if="jobtemplate.deleted_at" class="text-gray-500 mx-5"><i class="fad fa-trash mr-1"></i></span>
                    </td>

                    <td class="px-3 border-t">
                        <inertia-link class="py-4 flex items-center focus:font-bold" :href="route('admin.jobtemplates.show', jobtemplate.id)" tabindex="-1">
                            {{ jobtemplate.id }}
                        </inertia-link>
                    </td>

                    <td class="border-t">
                        <inertia-link class="py-4 flex items-center" :href="route('admin.jobtemplates.show', jobtemplate.id)" tabindex="-1">
                            <div v-if="jobtemplate.created_at">
                                {{ moment(jobtemplate.created_at).format("DD-MM-YYYY") }}
                            </div>
                        </inertia-link>
                    </td>

                    <td class="border-t">
                        <inertia-link class="py-4 flex items-center focus:text-teal-800 truncate" :href="route('admin.jobtemplates.show', jobtemplate.id)">
                            {{ jobtemplate.title | truncate(50) }}
                        </inertia-link>
                    </td>

                    <td class="px-3 border-t">
                        <inertia-link class="py-4 flex items-center focus:font-bold" :href="route('admin.jobtemplates.show', jobtemplate.id)" tabindex="-1">
                            {{ jobtemplate.ignore ? 'negeer' : '' }}
                        </inertia-link>
                    </td>

                    <td class="text-right pr-5 border-t">
                        <div v-if="!jobtemplate.deleted_at">
                            <link-button :href="route('admin.jobtemplates.edit', jobtemplate.id)" icon="pencil" class="hover:bg-white"></link-button>
                            <simple-button icon="trash" class="hover:bg-red-600 hover:text-white" @click="deleteJobTemplates(jobtemplate)"></simple-button>
                        </div>
                    </td>
                </tr>
            </table>

            <pagination :links="jobtemplates.links" />

        </div>
    </div>
</template>

<script>
import _ from "lodash";
import Layout from "@/admin/Shared/Layout";
import SortIcon from "@/admin/Shared/SortIcon";
import Pagination from "@/admin/Shared/Pagination";
import SearchFilter from "@/admin/Shared/SearchFilter";
import ActiveFilters from "@/admin/Shared/ActiveFilters";
import NumResults from "@/admin/Shared/NumResults";
import Breadcrumbs from "@/admin/Shared/Breadcrumbs";
import LinkButton from "@/admin/Shared/LinkButton";
import SimpleButton from "@/admin/Shared/SimpleButton";
import Helpers from "@/admin/Mixins/GeneralHelpers.js";

export default {
    metaInfo: { title: "JobTemplates" },

    layout: Layout,

    remember: "form",

    mixins: [Helpers],

    components: {
        Pagination,
        SearchFilter,
        ActiveFilters,
        SortIcon,
        NumResults,
        Breadcrumbs,
        LinkButton,
        SimpleButton
    },

    props: {
        jobtemplates: Object,
        filters: Object
    },
    data() {
        return {
            form: {
                trashed: this.filters.trashed,
                active: this.filters.active,
                search: this.filters.search,
                orderBy: this.filters.orderBy,
                sortOrder: this.filters.sortOrder
            }
        };
    },
    methods: {
        deleteJobTemplates(jobtemplate) {
            this.$inertia.delete(this.route("admin.jobtemplates.destroy", jobtemplate.id), {
                preserveScroll: true
            });
        },

        reloadData() {
            let queryParams = _.pickBy(this.form);
            this.$inertia.replace(this.route("admin.jobtemplates.index", Object.keys(queryParams).length ? queryParams : { remember: "forget" }));
        },

        sort(col) {
            this.form.sortOrder = this.getNewSortOrder(col, this.form.orderBy, this.form.sortOrder);
            this.form.orderBy = col;
            this.reloadData();
        },

        submitSearch: _.debounce(function() {
            this.reloadData();
        }, 250),

        reset() {
            this.form = _.mapValues(this.form, () => null);
            this.reloadData();
        },

        removeFilter(filterName) {
            this.form[filterName] = null;
            this.reloadData();
        }
    }
};
</script>
